<e-card2 class="game-panel-card" padding="3">
  <header class="game-panel-header">
    <div class="game-panel-header-row">
      <h3 class="game-panel-title">{{ t('humanDesign.games.channelGuessing.title') }}</h3>
      @if (!isFinished()) {
        <button
          eBtn
          type="button"
          size="sm"
          squared
          [color]="audioEnabled() ? 'primary' : 'highlight'"
          [icon]="audioEnabled() ? 'volume-high' : 'volume-xmark'"
          [tooltip]="t(audioEnabled() ? 'humanDesign.games.channelGuessing.audioOn' : 'humanDesign.games.channelGuessing.audioOff')"
          (click)="toggleAudio()"
        ></button>
      }
    </div>
    @if (!isFinished()) {
      @if (isMemoryMode()) {
        <p class="game-panel-instruction">{{ t('humanDesign.games.channelGuessing.instructionMemory') }}</p>
      } @else {
        <p class="game-panel-instruction">{{ t('humanDesign.games.channelGuessing.instructionSelect') }}</p>
      }
    }
  </header>

  @if (isFinished()) {
    <div class="game-panel-finished" [class.is-perfect]="isPerfectGame()">
      @if (isPerfectGame()) {
        <i eIcon icon="trophy" class="game-panel-finished-trophy" size="2xl"></i>
      } @else {
        <i eIcon icon="award" class="game-panel-finished-trophy" size="2xl"></i>
      }
      <p class="game-panel-finished-title">{{ finishedTitle() }}</p>
      <div class="game-panel-stars" [attr.aria-label]="t('humanDesign.games.channelGuessing.starsAria', { level: scoreLevel() ?? 0 })">
        @for (_ of starIndices(); track $index) {
          <i eIcon icon="star" class="game-panel-star is-filled"></i>
        }
        @for (_ of emptyStarIndices(); track $index) {
          <i eIcon icon="star" class="game-panel-star is-empty"></i>
        }
      </div>
      <p class="game-panel-finished-score" [class.is-perfect]="isPerfectGame()">
        {{ scoreLabel() }} <span class="game-panel-finished-max">/ {{ maxPossibleScore }}</span>
      </p>
    </div>
  } @else {
    <div
      class="game-panel-question"
      [class.is-keynotes]="currentQuestionMode() === 'keynotes'"
      [class.is-memory]="isMemoryMode()"
    >
      @if (!isMemoryMode()) {
        @if (currentQuestionMode() === 'keynotes' && currentChannelTranslation(); as translation) {
          <span class="game-panel-question-keynote" aria-live="polite">
            <strong class="game-panel-question-keynote-name">{{ translation.name }}</strong>
            @if (translation.shortDescription) {
              <span class="game-panel-question-keynote-separator">–</span>
              <span class="game-panel-question-keynote-description">{{ translation.shortDescription }}</span>
            }
          </span>
        } @else {
          @if (currentChannelSortedGates(); as gates) {
            <span class="game-panel-question-gates" aria-live="polite">
              {{ gates[0] }} <span class="game-panel-question-gates-separator">–</span> {{ gates[1] }}
            </span>
          }
        }

        @if (selectedGate(); as selected) {
          <span class="game-panel-selection-chip" [attr.aria-label]="t('humanDesign.games.channelGuessing.selectedGateAria')">
            {{ selected }}
          </span>
        }
      }

      @if (isMemoryMode() && currentChannelSortedGates(); as gates) {
        <span class="game-panel-memory-gates" [attr.aria-label]="t('humanDesign.games.channelGuessing.memoryGatesAria')">
          <span class="game-panel-memory-gate-chip">{{ gates[0] }}</span>
          <span class="game-panel-memory-gate-separator">–</span>
          <span class="game-panel-memory-gate-chip">{{ gates[1] }}</span>
        </span>
      }

      @if (canRequestHint()) {
        <button
          eBtn
          type="button"
          size="sm"
          squared
          color="highlight"
          icon="lightbulb"
          iconColor="warning"
          [tooltip]="t('humanDesign.games.channelGuessing.hintTooltip')"
          tabindex="5"
          (click)="requestHint()"
        ></button>
      }

      @if (canSkip()) {
        <button
          eBtn
          type="button"
          size="sm"
          squared
          color="highlight"
          icon="forward"
          iconColor="primary-75"
          [tooltip]="t('humanDesign.games.channelGuessing.skipTooltip')"
          tabindex="6"
          (click)="requestSkip()"
        ></button>
      }

      @if (wrongAttemptIndices().length > 0) {
        <span class="game-panel-question-attempts" [attr.aria-label]="t('humanDesign.games.channelGuessing.wrongAttemptsAria')">
          @for (index of wrongAttemptIndices(); track index) {
            <i eIcon icon="times" class="game-panel-attempt-cross"></i>
          }
        </span>
      }
    </div>

    @if (isMemoryMode()) {
      <form class="game-panel-memory-form" (ngSubmit)="submitMemoryAnswer()">
        <label class="game-panel-memory-field">
          <span class="game-panel-memory-label">{{ t('humanDesign.games.channelGuessing.labelChannelOf') }}</span>
          <div class="game-panel-memory-input-row">
            <input
              #memoryNameInputRef
              type="text"
              class="form-control form-control-sm game-panel-memory-input"
              [class.is-invalid]="memoryNameWrong()"
              [class.is-locked]="memoryValidations().name"
              [readOnly]="memoryValidations().name"
              autocomplete="off"
              spellcheck="false"
              tabindex="1"
              [ngModel]="memoryNameInput()"
              (ngModelChange)="onMemoryNameInput($event)"
              (keydown)="onMemoryNameKeydown($event)"
              [ngModelOptions]="{ standalone: true }"
            />
            @if (memoryValidations().name) {
              <i eIcon icon="check" class="game-panel-memory-locked-icon" [attr.aria-label]="t('humanDesign.games.channelGuessing.validatedAria')"></i>
            } @else if (canValidateMemoryName()) {
              <button
                eBtn
                type="button"
                size="sm"
                squared
                color="highlight"
                icon="check"
                iconColor="primary-75"
                [tooltip]="t('humanDesign.games.channelGuessing.validateFieldTooltip')"
                tabindex="3"
                (click)="validateMemoryField('name')"
              ></button>
            }
          </div>
          @if (memoryNameAutocompleteSuggestion(); as suggestion) {
            <button
              type="button"
              class="game-panel-memory-suggestion"
              tabindex="-1"
              (click)="applyMemoryNameAutocomplete()"
              [title]="t('humanDesign.games.channelGuessing.suggestionTooltip')"
            >{{ suggestion }}</button>
          }
        </label>
        <label class="game-panel-memory-field">
          <span class="game-panel-memory-label">{{ t('humanDesign.games.channelGuessing.labelDesignOf') }}</span>
          <div class="game-panel-memory-input-row">
            <input
              type="text"
              class="form-control form-control-sm game-panel-memory-input"
              [class.is-invalid]="memoryDescriptionWrong()"
              [class.is-locked]="memoryValidations().description"
              [readOnly]="memoryValidations().description"
              autocomplete="off"
              spellcheck="false"
              tabindex="2"
              [ngModel]="memoryDescriptionInput()"
              (ngModelChange)="onMemoryDescriptionInput($event)"
              (keydown)="onMemoryDescriptionKeydown($event)"
              [ngModelOptions]="{ standalone: true }"
            />
            @if (memoryValidations().description) {
              <i eIcon icon="check" class="game-panel-memory-locked-icon" [attr.aria-label]="t('humanDesign.games.channelGuessing.validatedAria')"></i>
            } @else if (canValidateMemoryDescription()) {
              <button
                eBtn
                type="button"
                size="sm"
                squared
                color="highlight"
                icon="check"
                iconColor="primary-75"
                [tooltip]="t('humanDesign.games.channelGuessing.validateFieldTooltip')"
                tabindex="4"
                (click)="validateMemoryField('description')"
              ></button>
            }
          </div>
          @if (memoryDescriptionAutocompleteSuggestion(); as suggestion) {
            <button
              type="button"
              class="game-panel-memory-suggestion"
              tabindex="-1"
              (click)="applyMemoryDescriptionAutocomplete()"
              [title]="t('humanDesign.games.channelGuessing.suggestionTooltip')"
            >{{ suggestion }}</button>
          }
        </label>
        <div class="game-panel-memory-actions">
          <button
            eBtn
            type="submit"
            size="sm"
            color="primary"
            icon="check"
            tabindex="7"
          >{{ t('humanDesign.games.channelGuessing.answer') }}</button>
        </div>
      </form>
    }

    @if (!isMemoryMode() && hintRows().length > 0) {
      <ul class="game-panel-hints" [attr.aria-label]="t('humanDesign.games.channelGuessing.hintsAria')" aria-live="polite">
        @for (row of hintRows(); track row.kind) {
          <li class="game-panel-hint" [class]="'is-kind-' + row.kind">
            <span class="game-panel-hint-category">{{ t(row.categoryLabel) }}:</span>
            <span class="game-panel-hint-value">{{ row.value }}</span>
          </li>
        }
      </ul>
    }
  }

  @if (historyRows().length > 0) {
    <div class="game-panel-history-toolbar">
      <span class="game-panel-history-toolbar-label">{{ t('humanDesign.games.channelGuessing.historyTitle') }}</span>
      <button
        eBtn
        type="button"
        size="sm"
        squared
        [color]="showAllHintsInHistoryByDefault() ? 'primary' : 'highlight'"
        icon="eye"
        [tooltip]="t(showAllHintsInHistoryByDefault() ? 'humanDesign.games.channelGuessing.historyHintsHide' : 'humanDesign.games.channelGuessing.historyHintsShow')"
        tabindex="8"
        (click)="toggleShowAllHintsInHistoryByDefault()"
      ></button>
    </div>
    <ol class="game-panel-history" [attr.aria-label]="t('humanDesign.games.channelGuessing.historyTitle')">
      @for (row of historyRows(); track row.key) {
        <li
          class="game-panel-history-entry"
          [class]="'is-kind-' + row.kind"
          [class.is-expandable]="isHistoryRowExpandable(row)"
          [class.is-expanded]="isHistoryRowExpanded(row)"
          [attr.role]="isHistoryRowExpandable(row) ? 'button' : null"
          [attr.tabindex]="isHistoryRowExpandable(row) ? 0 : null"
          [attr.aria-expanded]="isHistoryRowExpandable(row) ? isHistoryRowExpanded(row) : null"
          (click)="toggleHistoryRowExpansion(row)"
          (keydown)="onHistoryRowKeydown($event, row)"
        >
          <span class="game-panel-history-label">{{ row.label }}</span>
          <span class="game-panel-history-points" [class]="row.pointsClass">{{ row.pointsLabel }}</span>
          <i eIcon [icon]="row.icon" class="game-panel-history-icon" [class]="row.iconClass"></i>
        </li>
        @if (isHistoryRowExpanded(row)) {
          <li class="game-panel-history-hints-entry" [attr.aria-label]="t('humanDesign.games.channelGuessing.hintInfoAria')">
            <ul class="game-panel-history-hints">
              @for (hint of hintRowsForHistoryRow(row); track hint.kind) {
                <li class="game-panel-hint" [class]="'is-kind-' + hint.kind">
                  <span class="game-panel-hint-category">{{ t(hint.categoryLabel) }}:</span>
                  <span class="game-panel-hint-value">{{ hint.value }}</span>
                </li>
              }
            </ul>
          </li>
        }
      }
    </ol>
  }

  <footer class="game-panel-footer">
    <p class="game-panel-score">{{ t('humanDesign.games.channelGuessing.score') }}: <strong>{{ scoreLabel() }}</strong></p>
    <button
      eBtn
      type="button"
      size="sm"
      color="highlight"
      icon="undo"
      iconColor="primary-75"
      tabindex="9"
      (click)="requestRestart()"
    >{{ t('humanDesign.games.channelGuessing.restart') }}</button>
  </footer>
</e-card2>
